import axiosInstance from "utils/axios";

export const getTemplates = async (params = {}) => {
  try {
    // Support both object and positional parameter styles for backward compatibility
    let requestParams;
    if (typeof params === 'object' && params !== null) {
      // Object style: { pageNumber, pageSize, channelId, search }
      requestParams = {
        pageNumber: params.pageNumber || 1,
        pageSize: params.pageSize || 10,
        channelId: params.channelId || undefined, // Don't default to 1, let backend handle
        search: params.search || ""
      };
    } else {
      // Fallback for positional parameters (for SMS modal compatibility)
      requestParams = {
        pageNumber: arguments[0] || 1,
        pageSize: arguments[1] || 10,
        channelId: arguments[2] || 1,
        search: arguments[3] || ""
      };
    }

    const response = await axiosInstance.post('/api/Template/GetTemplates', requestParams);

    const responseData = response.data;

    // Check if the response indicates failure FIRST
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      return {
        success: false,
        error: responseData?.message || "Failed to fetch templates",
        data: [],
        totalRecords: 0,
      };
    }

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let templatesData = [];

      // Parse the data if it's a JSON string
      if (typeof responseData.data === "string") {
        try {
          templatesData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing templates data:", parseError);
          templatesData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        templatesData = responseData.data;
      }

      // Normalize the template data structure for UI compatibility
      const normalizedData = templatesData.map(template => {
        return {
          // Keep original fields for backward compatibility first
          ...template,
          // Then override with normalized values
          id: template.TemplateId || template.id,
          name: template.Name || template.name,
          channelId: template.ChannelId || template.channelId,
          subject: template.Subject || template.subject || '',
          body: template.Body || template.body,
          mediaUrl: template.MediaUrl || template.mediaUrl || '',
          isActive: template.IsActive !== undefined ? template.IsActive : template.isActive,
          status: template.WhatsappStatus !== undefined ? template.WhatsappStatus : (template.Status !== undefined ? template.Status : template.status),
          // Force IsSystemTemplate to false to allow editing all templates
          IsSystemTemplate: template?.IsSystemTemplate,
          createdBy: template.CreatedBy || template.createdBy,
          createdDate: template.CreatedDate || template.createdDate,
          modifiedBy: template.ModifiedBy || template.modifiedBy,
          modifiedDate: template.ModifiedDate || template.modifiedDate,
          templateTypeId: template.TemplateTypeId || template.templateTypeId,
          attachments: template.Attachments || template.attachments || [],
        };
      });

      return {
        success: true,
        data: normalizedData,
        message: responseData.message || "Templates fetched successfully",
        totalRecords: responseData.totalRecords || normalizedData.length,
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch templates");
    }
  } catch (error) {
    console.error("Error fetching templates:", error);

    let errorMessage = "Failed to fetch templates";
    if (error.response) {
      const status = error.response.status;
      const serverMessage = error.response.data?.message || error.response.data?.error;
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status || null,
      data: [],
      totalRecords: 0,
    };
  }
};

// Extract variables from template body (e.g., {{email}}, {{name}})
export const extractTemplateVariables = (templateBody) => {
  if (!templateBody) return [];

  // Remove HTML tags first
  const textContent = templateBody.replace(/<[^>]*>/g, '');

  // Find all variables in {{variable}} format
  const variableMatches = textContent.match(/\{\{([^}]+)\}\}/g);

  if (!variableMatches) return [];

  // Extract variable names and remove duplicates
  const variables = variableMatches.map(match => match.replace(/[{}]/g, ''));
  return [...new Set(variables)];
};

// Replace variables in template with actual values
export const replaceTemplateVariables = (templateBody, variables) => {
  if (!templateBody || !variables) return templateBody;

  let processedTemplate = templateBody;

  Object.keys(variables).forEach(key => {
    const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
    processedTemplate = processedTemplate.replace(regex, variables[key] || '');
  });

  return processedTemplate;
};

// Convert HTML template to plain text for SMS
export const htmlToPlainText = (html) => {
  if (!html) return '';

  // Replace common HTML elements with text equivalents
  let text = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n')
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // Clean up extra whitespace and newlines
  text = text
    .replace(/\n\s*\n/g, '\n') // Remove empty lines
    .replace(/^\s+|\s+$/g, '') // Trim
    .replace(/\s+/g, ' '); // Replace multiple spaces with single space

  return text;
};

// Create a new template
// export const createTemplate = async (templateData) => {
//   try {
//     const response = await axiosInstance.post('/api/Template', templateData);

//     const responseData = response.data;

//     // Check if the response indicates failure FIRST
//     if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
//       throw new Error(responseData?.message || "Failed to create template");
//     }

//     // Check for success status
//     if (responseData.statusCode === 200 && responseData.status === "Success") {
//       return {
//         success: true,
//         data: responseData.data,
//         message: responseData.message || "Template created successfully",
//       };
//     }

//     // If we get here, return success with available data
//     return {
//       success: true,
//       data: responseData.data,
//       message: responseData.message || "Template created successfully",
//     };
//   } catch (error) {
//     console.error("Error creating template:", error);

//     let errorMessage = "Failed to create template";
//     if (error.response) {
//       const status = error.response.status;
//       const serverMessage = error.response.data?.message || error.response.data?.error;
//       if (status >= 400 && status < 500) {
//         errorMessage = serverMessage || `Client error: ${status}`;
//       } else if (status >= 500) {
//         errorMessage = serverMessage || `Server error: ${status}`;
//       }
//     } else if (error.request) {
//       errorMessage = "Network error: Unable to connect to server";
//     } else {
//       errorMessage = error.message || "Unknown error occurred";
//     }

//     throw new Error(errorMessage);
//   }
// };
export const createTemplate = async (templateData) => {
  try {
    const formData = new FormData();

    // Flatten all template fields with 'Template.' prefix
    formData.append('Template.Name', templateData.name);
    formData.append('Template.ChannelId', templateData.channelId.toString());
    formData.append('Template.Subject', templateData.subject || '');
    formData.append('Template.Body', templateData.body);
    formData.append('Template.MediaUrl', templateData.mediaUrl || '');
    formData.append('Template.IsActive', templateData.isActive.toString());
    formData.append('Template.CreatedBy', templateData.createdBy);
    formData.append('Template.ModifiedBy', templateData.modifiedBy);

    // Add optional fields if they exist
    if (templateData.templateId) {
      formData.append('Template.TemplateId', templateData.templateId);
    }
    if (templateData.companyId) {
      formData.append('Template.CompanyId', templateData.companyId);
    }
    if (templateData.templateTypeId) {
      formData.append('Template.TemplateTypeId', templateData.templateTypeId);
    }

    // Append attachments
    if (templateData.attachments && templateData.attachments.length > 0) {
      templateData.attachments.forEach((file) => {
        if (file instanceof File) {
          formData.append('Attachments', file);
        }
      });
    }

    const response = await axiosInstance.post('/api/Template/Create-template', formData, {
      // Content-Type handled by axios interceptor for FormData
      timeout: 60000, // 1 minute timeout for template with attachments
    });

    const responseData = response.data;

    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || "Failed to create template");
    }

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Template created successfully",
      };
    }

    return {
      success: true,
      data: responseData.data,
      message: responseData.message || "Template created successfully",
    };
  } catch (error) {
    console.error("Error creating template:", error);

    let errorMessage = "Failed to create template";
    if (error.response) {
      const status = error.response.status;
      const serverMessage = error.response.data?.message || error.response.data?.error;

      if (error.response.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorKeys = Object.keys(validationErrors);
        if (errorKeys.length > 0) {
          errorMessage = validationErrors[errorKeys[0]][0];
        }
      } else if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    throw new Error(errorMessage);
  }
};

// Update an existing template
export const updateTemplate = async (templateId, templateData) => {
  try {
    const formData = new FormData();

    // Flatten all template fields with 'Template.' prefix for API structure
    formData.append('Template.TemplateId', templateId);
    formData.append('Template.Name', templateData.name);
    formData.append('Template.ChannelId', templateData.channelId.toString());
    formData.append('Template.Subject', templateData.subject || '');
    formData.append('Template.Body', templateData.body);
    formData.append('Template.MediaUrl', templateData.mediaUrl || '');
    formData.append('Template.IsActive', templateData.isActive.toString());
    formData.append('Template.ModifiedBy', templateData.modifiedBy);

    // Add optional fields if they exist
    if (templateData.companyId) {
      formData.append('Template.CompanyId', templateData.companyId);
    }
    if (templateData.templateTypeId) {
      formData.append('Template.TemplateTypeId', templateData.templateTypeId);
    }
    if (templateData.createdBy) {
      formData.append('Template.CreatedBy', templateData.createdBy);
    }

    // Helper function to get MIME type from file extension
    const getMimeTypeFromExtension = (fileName) => {
      const extension = fileName.split('.').pop().toLowerCase();
      const mimeTypes = {
        // Documents
        'pdf': 'application/pdf',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'ppt': 'application/vnd.ms-powerpoint',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'txt': 'text/plain',
        'csv': 'text/csv',
        // Images
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'bmp': 'image/bmp',
        'svg': 'image/svg+xml',
        'webp': 'image/webp',
        // Archives
        'zip': 'application/zip',
        'rar': 'application/x-rar-compressed',
        '7z': 'application/x-7z-compressed',
        // Others
        'json': 'application/json',
        'xml': 'application/xml',
      };
      return mimeTypes[extension] || 'application/octet-stream';
    };

    // Build attachments array with proper structure: array of objects with attachmentId and file
    const attachmentsArray = [];

    // Process existing attachments - create placeholder File objects with correct MIME types
    if (templateData.existingAttachments && templateData.existingAttachments.length > 0) {
      templateData.existingAttachments.forEach((attachment) => {
        // Get the attachment ID (could be AttachmentId or attachmentId)
        const attachmentId = attachment.AttachmentId || attachment.attachmentId;
        const fileName = attachment.FileName || attachment.fileName || attachment.name || 'attachment';

        if (attachmentId) {
          // For existing attachments, create a placeholder File object with correct MIME type
          // The backend should recognize the attachmentId and use the existing file
          const mimeType = getMimeTypeFromExtension(fileName);
          const placeholderBlob = new Blob([''], { type: mimeType });
          const placeholderFile = new File([placeholderBlob], fileName, { type: mimeType });

          attachmentsArray.push({
            attachmentId: attachmentId,
            file: placeholderFile
          });
        }
      });
    }

    // Process NEW file attachments - these are File objects without attachmentId
    if (templateData.attachments && templateData.attachments.length > 0) {
      templateData.attachments.forEach((file) => {
        if (file instanceof File) {
          // For new files, we don't have an attachmentId yet, so we send empty GUID
          attachmentsArray.push({
            attachmentId: "", // Empty GUID for new files
            file: file
          });
        }
      });
    }

    // Append attachments array to FormData
    attachmentsArray.forEach((attachment, index) => {
      formData.append(`Attachments[${index}].attachmentId`, attachment.attachmentId);
      formData.append(`Attachments[${index}].file`, attachment.file);
    });

    const response = await axiosInstance.post('/api/Template/UpdateTemplate', formData, {
      // Content-Type handled by axios interceptor for FormData
      timeout: 60000, // 1 minute timeout for template with attachments
    });

    const responseData = response.data;

    // Check if the response indicates failure FIRST
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || "Failed to update template");
    }

    // Check for success status
    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Template updated successfully",
      };
    }

    // If we get here, return success with available data
    return {
      success: true,
      data: responseData.data,
      message: responseData.message || "Template updated successfully",
    };
  } catch (error) {
    console.error("Error updating template:", error);

    let errorMessage = "Failed to update template";
    if (error.response) {
      const status = error.response.status;
      const serverMessage = error.response.data?.message || error.response.data?.error;

      if (error.response.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorKeys = Object.keys(validationErrors);
        if (errorKeys.length > 0) {
          errorMessage = validationErrors[errorKeys[0]][0];
        }
      } else if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    throw new Error(errorMessage);
  }
};

// Update template status only (optimized endpoint)
export const updateTemplateStatus = async (templateId, isActive) => {
  try {

    const payload = {
      templateId: String(templateId).trim(),
      isActive: Boolean(isActive)
    };


    const response = await axiosInstance.post('/api/Template/UpdateTemplateStatus', payload);

    const responseData = response.data;

    // Check for failure status
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || 'Failed to update template status');
    }

    // Check for success status
    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        message: responseData.message || 'Template status updated successfully',
        data: responseData.data
      };
    }

    // If we get here, return success with available data
    return {
      success: true,
      message: responseData.message || 'Template status updated successfully',
      data: responseData.data
    };
  } catch (error) {
    console.error('Error updating template status:', error);

    let errorMessage = "Failed to update template status";
    if (error.response) {
      const status = error.response.status;
      const serverMessage = error.response.data?.message || error.response.data?.error;
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    throw new Error(errorMessage);
  }
};

// Delete a template
export const deleteTemplate = async (templateId) => {
  try {
    const response = await axiosInstance.delete(`/api/Template/${templateId}`);

    const responseData = response.data;

    // Check if the response indicates failure FIRST
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || "Failed to delete template");
    }

    // Check for success status
    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Template deleted successfully",
      };
    }

    // If we get here, return success with available data
    return {
      success: true,
      data: responseData.data,
      message: responseData.message || "Template deleted successfully",
    };
  } catch (error) {
    console.error("Error deleting template:", error);

    let errorMessage = "Failed to delete template";
    if (error.response) {
      const status = error.response.status;
      const serverMessage = error.response.data?.message || error.response.data?.error;
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    throw new Error(errorMessage);
  }
};